// Bileli · modules/media/model — the finalize wait loop's timing rules, as pure functions.
//
// why a separate file: the loop itself is I/O, but the decisions inside it are the part that was wrong.
// The old ceiling was `60 attempts × 5s`, which is not a duration — it is a product of two numbers, only
// one of which we control, and it SHRANK whenever the backend sent a shorter Retry-After. Meanwhile the
// real waits are 10–17 minutes, because Bunny's library sits on the free shared encoder and staying there
// is a settled decision. A wall-clock deadline says what we actually mean.

/**
 * How long we are willing to wait for one encode, from the first attempt.
 *
 * 30 minutes against observed 10–17 minute waits — generous on purpose. Reaching it is NOT an error and
 * does not fail the upload: the engine re-arms on a later tick, the pendingFinalize record has a 24h TTL,
 * and the backend now settles assets via the Bunny webhook without the tab open at all.
 */
export const FINALIZE_DEADLINE_MS = 30 * 60_000;

/** Fallback cadence when the server sends no usable Retry-After. */
export const DEFAULT_POLL_MS = 5_000;
/** Ceiling on a server-sent Retry-After, so one bad header cannot park the poll for an hour. */
export const MAX_POLL_S = 60;
/**
 * Re-POST finalize every Nth tick while waiting.
 *
 * The GET is the cheap readiness probe (a stored-row read); finalize is a WRITE that calls Bunny on every
 * invocation. But finalize is also the only thing that reports `queued` / `encodeProgress`, so the UI
 * would never flip from "queued" to a real percentage if we only ever GET. Every 4th tick keeps vendor
 * round-trips roughly at the old cadence's expense while the cheap probe does the actual waiting.
 */
export const REFRESH_PROGRESS_EVERY = 4;

/** Whether this tick should re-POST finalize to refresh the queued/progress display. */
export function shouldRefreshProgress(tick: number): boolean {
  return tick > 0 && tick % REFRESH_PROGRESS_EVERY === 0;
}

/** Deadline test, taken as an explicit pair so it can be tested without faking a clock. */
export function isPastDeadline(startedAt: number, now: number, deadlineMs = FINALIZE_DEADLINE_MS): boolean {
  return now - startedAt >= deadlineMs;
}

/**
 * The next wait, from the server's `Retry-After` (seconds).
 *
 * The backend now sends an ADAPTIVE value — long while queued, short while transcoding — so honouring it
 * is what makes a queued video poll gently and a transcoding one poll tightly. We only clamp: a missing,
 * malformed or non-positive header falls back, and anything above MAX_POLL_S is capped.
 */
export function nextPollDelayMs(retryAfterHeader: string | null | undefined): number {
  const seconds = Number.parseInt(retryAfterHeader ?? '', 10);
  if (!Number.isFinite(seconds) || seconds <= 0) return DEFAULT_POLL_MS;
  return Math.min(seconds, MAX_POLL_S) * 1000;
}

/** Terminal states from the cheap GET probe. Anything else means "still waiting". */
export type UploadProbe = 'ready' | 'failed' | 'pending';

/**
 * Read the GET's `state` without trusting it to be one of the three we know.
 *
 * why unknown → 'pending': a state we do not recognise is not a reason to declare an upload dead. The
 * engine re-arms, and a future server state degrades into "keep waiting" rather than a false failure.
 */
export function probeFromState(state?: string | null): UploadProbe {
  if (state === 'READY') return 'ready';
  if (state === 'FAILED') return 'failed';
  return 'pending';
}
